import { useState, useEffect, useCallback } from "react";
import { Type, Euro, Newspaper, FilePlus2, Plus, Trash2, Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { FIELD_GROUPS } from "../data/editableFields";
import { ArticlesManager, PagesManager } from "./ContentEditors";

const inputCls =
  "w-full rounded-xl border border-white/10 bg-navy px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold";

function TextsManager({ passcode }) {
  const [values, setValues] = useState({});
  useEffect(() => {
    api.getContent().then((r) => setValues(r.content || {})).catch(() => {});
  }, []);

  const save = async (key) => {
    try {
      await api.adminPutContent(passcode, { key, value: values[key] || "" });
      toast.success((values[key] || "").trim() ? "Enregistré — visible immédiatement sur le site." : "Champ vidé : texte d'origine restauré.");
    } catch { toast.error("Erreur d'enregistrement."); }
  };

  const reset = async (key) => {
    setValues({ ...values, [key]: "" });
    try {
      await api.adminPutContent(passcode, { key, value: "" });
      toast.success("Champ vidé : texte d'origine restauré.");
    } catch { toast.error("Erreur d'enregistrement."); }
  };

  return (
    <div className="space-y-10">
      <p className="rounded-2xl border border-gold/25 bg-navy-elevated p-4 text-sm text-slate-300">
        Modifiez ici les textes et images du site. <span className="text-gold">Champ vide = contenu d'origine.</span> Les changements sont visibles immédiatement sur le site, sans redéploiement.
      </p>
      {FIELD_GROUPS.map((g) => (
        <div key={g.group}>
          <h3 className="font-serif text-2xl text-gold">{g.group}</h3>
          <div className="mt-4 space-y-4">
            {g.fields.map((f) => (
              <div key={f.key} className="rounded-2xl border border-white/10 bg-navy-surface/60 p-5">
                <p className="text-sm font-medium text-slate-200">{f.label}</p>
                <p className="mt-0.5 font-mono text-[11px] text-slate-500">{f.key}</p>
                {f.type === "textarea" ? (
                  <textarea rows={3} data-testid={`cms-field-${f.key}`} value={values[f.key] || ""}
                    onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                    placeholder="Vide = texte d'origine du site" className={`${inputCls} mt-3`} />
                ) : (
                  <input data-testid={`cms-field-${f.key}`} value={values[f.key] || ""}
                    onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                    placeholder={f.type === "image" ? "https://… (collez l'URL d'une image)" : "Vide = texte d'origine du site"}
                    className={`${inputCls} mt-3`} />
                )}
                {f.type === "image" && (values[f.key] || "") && (
                  <img src={values[f.key]} alt="Aperçu" className="mt-3 h-20 rounded-lg border border-white/10 object-cover" />
                )}
                <div className="mt-3 flex gap-2">
                  <button onClick={() => save(f.key)} data-testid={`cms-save-${f.key}`}
                    className="flex items-center gap-1.5 rounded-full bg-gold px-4 py-1.5 text-xs font-semibold text-navy hover:bg-gold-light">
                    <Save className="h-3.5 w-3.5" /> Enregistrer
                  </button>
                  <button onClick={() => reset(f.key)} data-testid={`cms-reset-${f.key}`}
                    className="flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-1.5 text-xs text-slate-300 hover:border-gold/50">
                    <RotateCcw className="h-3.5 w-3.5" /> Vider (origine)
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function PricingManager({ passcode }) {
  const [tiers, setTiers] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.getTiers().then((r) => { setTiers(r.tiers || []); setNote(r.note || ""); }).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const upd = (i, field, val) => setTiers(tiers.map((t, j) => (j === i ? { ...t, [field]: val } : t)));

  const save = async () => {
    setBusy(true);
    try {
      await api.adminPutPricing(passcode, {
        tiers: tiers.map((t) => ({ min: Number(t.min), max: Number(t.max), price: Number(t.price) })),
        note,
      });
      toast.success("Tarifs enregistrés — le calculateur les applique immédiatement.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'enregistrement.");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl space-y-5">
      <p className="rounded-2xl border border-gold/25 bg-navy-elevated p-4 text-sm text-slate-300">
        Paliers du calculateur (prix au m² par mois selon le nombre de logements). <span className="text-gold">Effet immédiat sur la page Tarifs.</span>
      </p>
      <div className="space-y-3" data-testid="pricing-editor">
        {tiers.map((t, i) => (
          <div key={i} className="flex flex-wrap items-center gap-3 rounded-2xl border border-white/10 bg-navy-surface/60 p-4">
            <span className="text-sm text-slate-400">De</span>
            <input type="number" min="1" value={t.min} onChange={(e) => upd(i, "min", e.target.value)} className={`${inputCls} w-24`} data-testid={`tier-min-${i}`} />
            <span className="text-sm text-slate-400">à</span>
            <input type="number" min="1" value={t.max} onChange={(e) => upd(i, "max", e.target.value)} className={`${inputCls} w-24`} data-testid={`tier-max-${i}`} />
            <span className="text-sm text-slate-400">logements :</span>
            <input type="number" step="0.01" min="0.01" value={t.price} onChange={(e) => upd(i, "price", e.target.value)} className={`${inputCls} w-28`} data-testid={`tier-price-${i}`} />
            <span className="text-sm text-slate-400">€/m²</span>
            <button onClick={() => setTiers(tiers.filter((_, j) => j !== i))} data-testid={`tier-del-${i}`}
              className="ml-auto rounded-full border border-white/15 p-2 text-slate-400 hover:border-red-400/50 hover:text-red-300" aria-label="Supprimer ce palier">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
      <button onClick={() => setTiers([...tiers, { min: 1, max: 10, price: 0.85 }])} data-testid="tier-add"
        className="flex items-center gap-2 rounded-full border border-gold/50 px-4 py-2 text-sm text-gold hover:bg-gold/10">
        <Plus className="h-4 w-4" /> Ajouter un palier
      </button>
      <div>
        <p className="mb-2 text-sm text-slate-300">Note affichée sous la grille</p>
        <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="pricing-note" className={inputCls} />
      </div>
      <button onClick={save} disabled={busy} data-testid="pricing-save"
        className="flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-semibold text-navy hover:bg-gold-light disabled:opacity-60">
        <Save className="h-4 w-4" /> Enregistrer les tarifs
      </button>
    </div>
  );
}

const TABS = [
  ["textes", "Textes & images", Type],
  ["tarifs", "Tarifs", Euro],
  ["articles", "Articles", Newspaper],
  ["pages", "Pages libres", FilePlus2],
];

export default function ContentManager({ passcode }) {
  const [tab, setTab] = useState("textes");
  return (
    <div data-testid="content-manager">
      <div className="flex flex-wrap gap-2">
        {TABS.map(([id, label, Icon]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`cms-tab-${id}`}
            className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-colors ${tab === id ? "bg-gold text-navy font-medium" : "border border-white/15 text-slate-300 hover:text-gold"}`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>
      <div className="mt-8">
        {tab === "textes" && <TextsManager passcode={passcode} />}
        {tab === "tarifs" && <PricingManager passcode={passcode} />}
        {tab === "articles" && <ArticlesManager passcode={passcode} />}
        {tab === "pages" && <PagesManager passcode={passcode} />}
      </div>
    </div>
  );
}
